import type {
  CreateBookingSimplifiedCostRequest,
  CreateBookingSimplifiedCostResponse,
  SupportedCurrency,
} from '../Types/bookingSimplifiedCost.types';
import { supportedBookingSimplifiedCostCurrencies } from '../Types/bookingSimplifiedCost.types';

export class BookingSimplifiedCostSchemaMismatchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BookingSimplifiedCostSchemaMismatchError';
  }
}

const isFinitePositiveNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const normalizeAssignmentTagId = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (isFinitePositiveNumber(value)) {
    return value;
  }

  throw new BookingSimplifiedCostSchemaMismatchError(
    'Tag przypisania kosztu ma nieprawidłowy format względem kontraktu.'
  );
};

export const isSupportedCurrency = (value: unknown): value is SupportedCurrency =>
  typeof value === 'string' &&
  (supportedBookingSimplifiedCostCurrencies as readonly string[]).includes(value);

export const assertSupportedCurrency = (value: unknown): SupportedCurrency => {
  if (!isSupportedCurrency(value)) {
    throw new BookingSimplifiedCostSchemaMismatchError(
      'Wykryto nieobsługiwaną walutę poza kontraktem SupportedCurrency.'
    );
  }

  return value;
};

export const mapCreateBookingSimplifiedCostRequest = (
  request: CreateBookingSimplifiedCostRequest
): CreateBookingSimplifiedCostRequest => {
  const hasAssignmentTagId = Object.prototype.hasOwnProperty.call(request, 'assignmentTagId');
  const hasTagIds = Object.prototype.hasOwnProperty.call(request, 'tagIds');

  return {
    companyId: request.companyId,
    issueDate: request.issueDate,
    totalGross: request.totalGross,
    currency: assertSupportedCurrency(request.currency),
    description: request.description.trim(),
    ...(hasTagIds ? { tagIds: Array.from(new Set(request.tagIds ?? [])) } : {}),
    ...(hasAssignmentTagId
      ? { assignmentTagId: normalizeAssignmentTagId(request.assignmentTagId) }
      : {}),
  };
};

export const parseCreateBookingSimplifiedCostResponse = (
  response: unknown
): CreateBookingSimplifiedCostResponse => {
  if (typeof response !== 'object' || response === null) {
    throw new BookingSimplifiedCostSchemaMismatchError(
      'Odpowiedź quick-create uproszczonego kosztu ma nieprawidłowy format.'
    );
  }

  const data = response as Record<string, unknown>;

  if (
    !isFinitePositiveNumber(data.expenseInvoiceId) ||
    !isFinitePositiveNumber(data.bookingId) ||
    !isFinitePositiveNumber(data.allocatedAmount)
  ) {
    throw new BookingSimplifiedCostSchemaMismatchError(
      'Odpowiedź quick-create uproszczonego kosztu nie spełnia kontraktu write API.'
    );
  }

  return {
    expenseInvoiceId: data.expenseInvoiceId,
    bookingId: data.bookingId,
    allocatedAmount: data.allocatedAmount,
  };
};

